/**
 * NMS Deal Nudge
 * Sticky pill shown to every visitor of a page that has the sale sections. Two states:
 *   flash - a flash-deal slot is live (the hourly section has a LIVE tab)  -> scrolls to Flash deals
 *   dod   - no slot is live                                                  -> scrolls to Deal of the day
 * The state is read from the hourly flash deals section itself, so the slot times set there
 * (start / end time per slot) are the only schedule to maintain. See sections/nms-deal-nudge.liquid.
 * Entrance: the icon pops in first (is-visible), then the pill widens into the gap between the other
 * floating buttons and shows its content (is-expanded). A close button sits centred above it.
 * Position: on mobile the pill sits in the bottom row between the left and right floating buttons
 * (WhatsApp / search); --nudge-left / --nudge-right are measured from those buttons, including ones
 * that are still hidden (they fade in on scroll).
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-deal-nudge]');
  if (!root || root.dataset.nudgeBound) return;
  root.dataset.nudgeBound = '1';

  var DISMISS_KEY = 'nmsDealNudgeDismissed';
  var EXPAND_DELAY = 700; // ms the icon is shown alone before the pill opens
  var GAP = 12; // px between the pill and a neighbouring button
  var MIN_INSET = 72; // 16px edge + 44px button + 12px gap, used even if a button is not found

  function safeGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage can be blocked; the nudge still works for this page view */
    }
  }

  if (safeGet(DISMISS_KEY) === '1') return;

  var titleEl = root.querySelector('[data-deal-nudge-title]');
  var nameEl = root.querySelector('[data-deal-nudge-sub-name]');
  var timeEl = root.querySelector('[data-deal-nudge-sub-time]');
  var goBtn = root.querySelector('[data-deal-nudge-go]');
  var closeBtn = root.querySelector('[data-deal-nudge-close]');
  var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getFlash() {
    return document.querySelector('[data-nms-hourly-flash-deals]');
  }

  function getDod() {
    return document.querySelector('nms-sale-featured-product, .nms-sale-featured-product');
  }

  function isRendered(el) {
    return !!(el && el.getClientRects().length);
  }

  function liveTab() {
    var flash = getFlash();
    return flash && isRendered(flash) ? flash.querySelector('.nms-hourly-flash-deals__tab--live') : null;
  }

  // flash while a slot is live, otherwise Deal of the day (if that section is on the page)
  function currentState() {
    if (liveTab()) return 'flash';
    if (isRendered(getDod())) return 'dod';
    return null;
  }

  function targetFor(state) {
    return state === 'flash' ? getFlash() : getDod();
  }

  function setText(el, value) {
    if (el.textContent !== value) el.textContent = value;
  }

  function setSub(name, time) {
    setText(nameEl, name);
    setText(timeEl, time);
  }

  function updateText(state) {
    if (state === 'dod') {
      setText(titleEl, root.dataset.dodTitle || 'Deal of the day');
      setSub(root.dataset.dodSub || '', '');
      return;
    }

    setText(titleEl, root.dataset.flashTitle || 'Flash deal is live');

    var flash = getFlash();
    var tabText = liveTab().querySelector('.nms-hourly-flash-deals__tab-text');
    var slotName = tabText ? tabText.textContent.replace(/^\s*LIVE\s*[·\-:]?\s*/i, '').trim() : '';

    var ribbon = liveTab().querySelector('.nms-hourly-flash-deals__tab-ribbon');
    var timeNode = ribbon ? ribbon.querySelector('.nms-hourly-flash-deals__tab-ribbon-time') : null;
    var time = timeNode ? timeNode.textContent.trim() : '';
    var hasTime = ribbon && !ribbon.hidden && time && time.indexOf('--') === -1;

    if (!slotName && !hasTime) {
      setSub(root.dataset.flashSub || '', '');
      return;
    }
    // The separator between slot name and time is drawn by CSS (::before), so it can be hidden on narrow screens
    setSub(slotName, hasTime ? 'ends in ' + time : '');
  }

  /*
   * Measure the other small fixed buttons in the bottom row (WhatsApp, search, app buttons...) and keep the
   * pill between them. Boxes are measured whether or not they are visible yet, because some (WhatsApp)
   * fade in only after the first scroll. Runs on show, on resize and once late widgets have loaded;
   * never on the 1s tick.
   */
  function setInsets() {
    var vw = document.documentElement.clientWidth;
    var vh = window.innerHeight;
    if (vw >= 750) {
      root.style.removeProperty('--nudge-left');
      root.style.removeProperty('--nudge-right');
      return;
    }
    var left = MIN_INSET;
    var right = MIN_INSET;
    var all = document.body.getElementsByTagName('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el === root || root.contains(el)) continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed' || cs.display === 'none') continue;
      var r = el.getBoundingClientRect();
      // Button-sized floating elements sitting in the bottom row only
      if (r.width < 8 || r.height < 8 || r.width > 140 || r.height > 140) continue;
      if (r.bottom < vh - 130 || r.top > vh) continue;
      if (r.left + r.width / 2 < vw / 2) left = Math.max(left, r.right + GAP);
      else right = Math.max(right, vw - r.left + GAP);
    }
    // Never squeeze the pill below a usable width
    var maxInset = Math.max(MIN_INSET, (vw - 190) / 2);
    root.style.setProperty('--nudge-left', Math.round(Math.min(left, maxInset)) + 'px');
    root.style.setProperty('--nudge-right', Math.round(Math.min(right, maxInset)) + 'px');
  }

  var shown = false;
  var ready = false;
  var reached = false;
  var variant = null;
  var expandTimer = null;
  var inView = { flash: false, dod: false };
  var viewTracked = { flash: false, dod: false };

  function track(eventName, state) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, nudge_target: state });
  }

  function show(state) {
    if (!shown) {
      shown = true;
      root.hidden = false;
      setInsets();
      // Force a style flush so the entrance transition runs: icon first, then the pill opens out
      void root.offsetWidth;
      root.classList.add('is-visible');
      window.clearTimeout(expandTimer);
      expandTimer = window.setTimeout(
        function () {
          if (shown) root.classList.add('is-expanded');
        },
        reducedMotion ? 0 : EXPAND_DELAY
      );
      // Late widgets (chat, search, app buttons) may appear after the pill: measure again once they have loaded
      window.setTimeout(function () {
        if (shown) setInsets();
      }, 2500);
    }
    if (!viewTracked[state]) {
      viewTracked[state] = true;
      track('deal_nudge_view', state);
    }
  }

  function hide() {
    if (!shown) return;
    shown = false;
    window.clearTimeout(expandTimer);
    root.classList.remove('is-visible');
    root.classList.remove('is-expanded');
    window.setTimeout(function () {
      if (!shown) root.hidden = true;
    }, 400);
  }

  function refresh() {
    if (reached || !ready) return;

    var state = currentState();
    if (!state) {
      hide();
      return;
    }

    // Visitor is already looking at the section the pill points to: done for this page view
    if (inView[state]) {
      reached = true;
      hide();
      return;
    }

    if (state !== variant) {
      variant = state;
      root.dataset.variant = state;
    }
    updateText(state);
    show(state);
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var flash = getFlash();
        var key = entry.target === flash ? 'flash' : 'dod';
        inView[key] = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      });
      refresh();
    },
    { threshold: [0, 0.2, 0.5] }
  );

  [getFlash(), getDod()].forEach(function (el) {
    if (el) observer.observe(el);
  });

  // The hourly section flips between live and locked on its own clock, so re-check every second (skipped while the tab is hidden)
  window.setInterval(function () {
    if (!document.hidden) refresh();
  }, 1000);

  var resizeTimer = null;
  window.addEventListener(
    'resize',
    function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (shown) setInsets();
      }, 200);
    },
    { passive: true }
  );

  goBtn.addEventListener('click', function () {
    var state = variant || currentState();
    var target = targetFor(state);
    if (!target) return;
    track('deal_nudge_click', state);
    target.style.scrollMarginTop = 'calc(var(--header-height, 64px) + 8px)';
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
  });

  if (closeBtn) {
    closeBtn.addEventListener('click', function () {
      safeSet(DISMISS_KEY, '1');
      track('deal_nudge_dismiss', variant);
      reached = true;
      hide();
    });
  }

  var delay = Math.max(0, parseFloat(root.dataset.delay) || 0) * 1000;
  window.setTimeout(function () {
    ready = true;
    refresh();
  }, delay);
})();
